
import { ParsedSunoOutput } from '@/types';

/**
 * Robust parser for HookHouse Markdown exports.
 * Designed to be lenient with line endings (CRLF/LF), heading levels, 
 * and whether content is strictly wrapped in code blocks.
 */
export const parseMarkdownToSunoOutput = (markdownContent: string): ParsedSunoOutput => {
  // Fix: Explicit return type handled, though syntax errors in body caused reporting issues
  const extractBlock = (text: string, label: string, expectCodeBlock: boolean = false): string => {
    const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    // Fix: Switched from template literal to string concatenation to safely handle triple backticks in regex without escaping errors
    // Pattern 1: Try to find a code block following a heading
    const codeBlockRegex = new RegExp(
      "(?:^|[\\r\\n])(?:#+\\s*)" + escapedLabel + "[\\r\\n]+(?:[\\s\\S]*?)```[^\\r\\n]*[\\r\\n]+([\\s\\S]*?)[\\r\\n]+```",
      'i'
    );
    const codeMatch = text.match(codeBlockRegex);
    if (codeMatch) return codeMatch[1].trim();

    // Fix: Verified pattern 2 regex structure for reliable fallback extraction
    // Pattern 2: Fallback to just text following a heading until the next heading
    const textRegex = new RegExp(
      "(?:^|[\\r\\n])(?:#+\\s*)" + escapedLabel + "[\\r\\n]+([\\s\\S]*?)(?=[\\r\\n]+#+|$)",
      'i'
    );
    const textMatch = text.match(textRegex);
    if (textMatch) return textMatch[1].trim();

    return "";
  };

  const extractTitleArtist = (text: string): string => {
    // Look for the main H1 title
    // Fix: refinement of regex literal to correctly handle newline lookahead
    const match = text.match(/^#\s*(.*?)(?=[\r\n]|$)/m);
    return match ? match[1].trim() : "";
  };

  const titleArtistRaw = extractTitleArtist(markdownContent);
  const summary = extractBlock(markdownContent, 'Summary');
  // Fix: Passing unescaped label string as the helper function handles its own escaping
  const styleBlueprint = extractBlock(markdownContent, 'Style (Production Blueprint)');
  
  // Try Suno Tags, then fallback to technical labels
  const sunoTags = extractBlock(markdownContent, 'Suno Tags') || extractBlock(markdownContent, 'Machine Tags');
  
  const lyricsWithTags = extractBlock(markdownContent, 'Lyrics');
  const javascriptCode = extractBlock(markdownContent, 'Suno Console JavaScript') || extractBlock(markdownContent, 'Automation Terminal');

  // Mandatory fields for a valid HookHouse session
  if (!titleArtistRaw || !summary || !styleBlueprint || !lyricsWithTags) {
    console.error("Parser failed to find required blocks:", {
      title: !!titleArtistRaw,
      summary: !!summary,
      blueprint: !!styleBlueprint,
      lyrics: !!lyricsWithTags
    });
    throw new Error("Markdown file is missing one or more required sections (Title, Summary, Blueprint, or Lyrics).");
  }

  // Handle multiple separator formats: //, /, —, –, " - "
  const separator = titleArtistRaw.includes('//') ? '//'
    : titleArtistRaw.includes(' — ') ? ' — '
    : titleArtistRaw.includes(' – ') ? ' – '
    : titleArtistRaw.includes(' - ') ? ' - '
    : '/';
  const parts = titleArtistRaw.split(separator).map(s => s.trim());
  const title = parts[0] || "Untitled";
  const artist = parts[1] || "Unknown Artist";

  const parsed: ParsedSunoOutput = {
    titleArtist: titleArtistRaw,
    summary: summary,
    styleBlueprint: styleBlueprint,
    style: sunoTags,
    excludedStyle: extractBlock(markdownContent, 'Excluded Styles'), 
    lyricsWithTags: lyricsWithTags,
    title: title,
    artist: artist,
    // Fix: Handle null/undefined lyricsWithTags to avoid runtime crash
    lyricsAlone: (lyricsWithTags || "")
      .replace(/\{LAYER-1 VOCAL PHYSICS\}[\s\S]*?\n\n/g, '')
      .replace(/\[.*?\]\n?/g, '')
      .trim(),
    javascriptCode: javascriptCode,
    fullResponse: markdownContent,
  };

  return parsed;
};
